import { Controller } from "@hotwired/stimulus";
import instantsearch from "instantsearch.js";
import { searchBox, hits, stats } from "instantsearch.js/es/widgets";
import { TypesenseService } from "./search/typesense_service";
import {
  RECENT_SEARCH_DEBOUNCE_VALUE,
  COMMIT_RECENT_SEARCH_RESULT_WAIT_TIME,
} from "./search/constants";
import {
  CourseSelectionStore,
  type StorageChangeListener,
} from "./stores/course_selection_store";

import { SearchAnalyticsHelper } from "./search/search_analytics_helper";
import { EventType } from "./search/types";

export default class extends Controller {
  static targets = ["originalGrid", "searchGrid"];

  static values = {
    config: Object,
  };

  static classes = ["hidden"];

  declare configValue: {
    apiKey: string;
    nearestNodeHost: string;
    nodesHosts: string[];
    courseIndexName: string;
    coursePresetName: string;
  };

  declare readonly originalGridTarget: HTMLElement;
  declare readonly searchGridTarget: HTMLElement;
  declare readonly hiddenClass: string;
  declare readonly timerId: ReturnType<typeof setTimeout>;
  declare readonly categorySlugsValue: string[];
  declare inputElement: HTMLInputElement;

  private typesenseService: TypesenseService | null = null;
  private search: ReturnType<typeof instantsearch> | null = null;
  private store: CourseSelectionStore = CourseSelectionStore.getInstance();
  private storeChangeListener: StorageChangeListener;
  private analyticsHelper: SearchAnalyticsHelper;
  courseHits: Map<number, Record<string, unknown>>;

  connect() {
    // Initialize store from existing DOM state
    this.store.initializeFromDOM(this.element);

    // Bind store change listener
    this.storeChangeListener = this.onStoreChange.bind(this);
    this.store.addListener(this.storeChangeListener);

    this.initializeTypesense();
  }

  disconnect() {
    // Clean up store listener
    this.store.removeListener(this.storeChangeListener);
  }

  private initializeTypesense() {
    if (!this.configValue) {
      return;
    }

    this.typesenseService = new TypesenseService({
      config: this.configValue,
      includeLessonIndex: false,
    });

    let timerInputId = this.timerId;

    this.search = this.typesenseService.getSearchInstance();

    this.analyticsHelper = new SearchAnalyticsHelper({
      getQuery: () => this.search.helper.state.query,
      getHitCounts: () => this.getHitCounts(),
    });

    this.search.addWidgets([
      searchBox({
        container: ".searchbox",
        placeholder: "Search BBC Maestro",
        queryHook(query, refine) {
          clearTimeout(timerInputId);
          timerInputId = setTimeout(
            () => refine(query),
            RECENT_SEARCH_DEBOUNCE_VALUE
          );
        },
        cssClasses: {
          form: "d-flex align-items-center",
          input: "flex-grow-1",
        },
      }),

      stats({
        container: ".stats",
        cssClasses: {
          root: "text-neutral-300 small",
        },
      }),

      hits({
        container: this.searchGridTarget,
        cssClasses: {
          list: "u-grid u-grid-fill",
        },
        templates: {
          item: (hit, { html }) => {
            const isChecked = this.store.isCourseSelected(hit.record_id);

            return html`
              <label
                class="position-relative mb-0"
                for="search_result_${hit.record_id}"
              >
                <input
                  type="checkbox"
                  id="search_result_${hit.record_id}"
                  value="${hit.record_id}"
                  class="vc-icon-input__field vc-icon-input__field--poster sr-only"
                  data-action="
                    change->course-picker#toggle
                    change->button-activate#toggle
                    change->button-swap#toggle
                  "
                  data-button-activate-target="item"
                  data-button-swap-target="item"
                  data-id="${hit.record_id}"
                  data-title="${hit.title}"
                  data-image-source="${hit.main_image.medium}"
                  ${isChecked ? "checked" : ""}
                />
                <div class="vc-icon-input__item vc-poster vc-poster--small">
                  <div class="vc-poster__background">
                    <picture>
                      <img alt="" src="${hit.main_image.medium}" />
                    </picture>
                  </div>
                  <div
                    class="vc-poster__copy text-center px-2 pb-2"
                    style="z-index: 1;"
                  >
                    <h3
                      class="font-md mb-0 text-neutral-50 bm-font-weight-medium"
                    >
                      ${hit.maestro.full_name}
                    </h3>
                    <p class="font-xs mt-2 mb-0 text-neutral-250">
                      ${hit.title}
                    </p>
                  </div>
                  <div
                    class="vc-icon-input__background d-none position-absolute h-100 w-100 align-items-center justify-content-center"
                  >
                    <svg
                      class="bm-icon-40 text-primary-400"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                      />
                    </svg>
                  </div>
                </div>
              </label>
            `;
          },
          empty: (_, { html }) => {
            return html`<div class="u-section-padding-top text-center">
              <h3 class="text-neutral-50 vc-h2">No results found</h3>
              <p class="mt-3 text-neutral-50">
                Try searching for another course, name or topic
              </p>
            </div>`;
          },
        },
      }),
    ]);

    this.search.start();

    // Show hits container when there are results
    this.search.on("render", () => {
      const hasQuery = this.search.helper.state.query?.trim().length > 0;
      const hasResults = this.search.helper.lastResults?.nbHits > 0;

      if (!hasQuery) {
        // Show original grid, hide search grid
        this.originalGridTarget.classList.remove("d-none");
        this.searchGridTarget.classList.add("d-none");
      } else if (hasResults) {
        // Hide original grid, show search grid
        this.originalGridTarget.classList.add("d-none");
        this.searchGridTarget.classList.remove("d-none");
      }

      // After render, sync any newly created checkboxes with store state
      this.store.syncToDOM(this.searchGridTarget);
    });

    this.inputElement = this.element.querySelector(".ais-SearchBox-input");
    let typingTimer: ReturnType<typeof setTimeout>;

    this.inputElement.addEventListener("input", () => {
      clearTimeout(typingTimer);

      typingTimer = setTimeout(() => {
        this.trackSearchResult();
      }, COMMIT_RECENT_SEARCH_RESULT_WAIT_TIME);
    });
  }

  trackSearchResult() {
    this.analyticsHelper.trackSearchResult(
      EventType.SearchResult,
      "freemium-search-bar"
    );
  }

  getHitCounts() {
    const num_hits_courses =
      this.search.renderState[this.configValue.courseIndexName].hits.results
        .nbHits;

    return {
      num_hits_courses,
    };
  }

  /**
   * Called whenever the store state changes
   * This ensures that search results stay in sync with the current selection
   */
  private onStoreChange() {
    // Sync all checkboxes in both grids
    this.store.syncToDOM(this.originalGridTarget);
    this.store.syncToDOM(this.searchGridTarget);
  }
}
